import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { canShareNatively, mailtoHref, shareNatively, smsHref, telegramHref, whatsappHref } from '../../lib/share';
import ui from '../../styles/ui.module.css';
import styles from './Secret.module.css';

interface ShareOptionsProps {
  /** Names the group for screen readers, e.g. "Share the link". */
  label: string;
  /** The message. `url`, when there is one, goes after it. */
  text: string;
  url?: string;
  emailSubject: string;
  /** Explain what the buttons do. Once per screen is enough. */
  showHint?: boolean;
}

/**
 * Share buttons that load nothing from anyone else (lib/share.ts): the
 * system share sheet where the browser has one, WhatsApp and Telegram
 * through their apps, then text and email.
 */
export default function ShareOptions({ label, text, url, emailSubject, showHint = false }: ShareOptionsProps) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const withUrl = (separator: string) => (url ? `${text}${separator}${url}` : text);
  const sheet = { title: emailSubject, text, url };
  const native = canShareNatively(sheet);

  async function onShare() {
    setFailed((await shareNatively(sheet)) === 'failed');
  }

  return (
    <div className={styles.share} role="group" aria-label={label}>
      <div className={styles.shareGrid}>
        {native && (
          <button type="button" className={styles.shareButton} onClick={() => void onShare()}>
            {t('secret.share.native')}
          </button>
        )}
        <a className={styles.shareButton} href={whatsappHref(withUrl(' '))}>
          WhatsApp
        </a>
        {/* Telegram's scheme needs a URL, so a passphrase on its own goes through Share… instead. */}
        {url && (
          <a className={styles.shareButton} href={telegramHref(url, text)}>
            Telegram
          </a>
        )}
        <a className={styles.shareButton} href={smsHref(withUrl(' '))}>
          {t('secret.share.sms')}
        </a>
        <a className={styles.shareButton} href={mailtoHref(emailSubject, withUrl('\n\n'))}>
          {t('secret.share.email')}
        </a>
      </div>
      {showHint && <p className={`${ui.hint} ${styles.shareHint}`}>{t(native ? 'secret.share.hintNative' : 'secret.share.hint')}</p>}
      <p role="status" className={failed ? ui.error : 'visually-hidden'}>
        {failed ? t('secret.share.failed') : ''}
      </p>
    </div>
  );
}
